/**
 * Dashboard-related type definitions
 * Centralized types for dashboard data structures
 *
 * @module types/dashboard
 */

/**
 * User profile for dashboard display
 */
export interface DashboardProfile {
  id: string;
  email: string;
  avatar_url?: string | null;
  consent_data_processing: boolean;
}

/**
 * Health metrics data structure (database schema)
 * Uses snake_case to match database column names
 */
export interface DashboardHealthMetrics {
  steps: number;
  sleep_hours: number;
  sleep_efficiency: number;
  sleep_deep_minutes: number;
  sleep_rem_minutes: number;
  sleep_start_time: string | null;
  active_minutes: number;
  heart_rate: number;
  hrv_rmssd: number;
  hrv_coverage: number;
  calories: number;
  distance: number;
  floors: number;
  weight: number;
  body_fat: number;
  oxygen_saturation: number;
  respiratory_rate: number;
  temperature: number;
  cardio_fitness: number;
  /** The actual date of the metric row (may differ from today if data is stale) */
  metric_source_date?: string | null;
  /** Deep HRV (RMSSD) for SOTA recovery scoring */
  hrv_deep_rmssd?: number;
}

/**
 * Combined dashboard data returned from batched RPC
 */
export interface DashboardData {
  profile: DashboardProfile | null;
  metrics: DashboardHealthMetrics;
  points: number;
  streak: number;
}

/**
 * Default health metrics values
 */
export const DEFAULT_DASHBOARD_METRICS: DashboardHealthMetrics = {
  steps: 0,
  sleep_hours: 0,
  sleep_efficiency: 0,
  sleep_deep_minutes: 0,
  sleep_rem_minutes: 0,
  sleep_start_time: null,
  active_minutes: 0,
  heart_rate: 0,
  hrv_rmssd: 0,
  hrv_coverage: 0,
  calories: 0,
  distance: 0,
  floors: 0,
  weight: 0,
  body_fat: 0,
  oxygen_saturation: 0,
  respiratory_rate: 0,
  temperature: 0,
  cardio_fitness: 0,
};

/**
 * Weekly check-in data for dashboard display
 */
export interface WeeklyCheckinData {
  weekly_win?: string;
  energy_level?: number;
}

/**
 * Aggregate, non-identifying segment insight surfaced on the dashboard and in
 * the coach. Lives here so data hooks do not have to import from a component.
 */
export interface PopulationInsightData {
  type: string;
  text: string;
  impact?: number;
  sample_size?: number;
}
